import type { BulkGeneratedBeddingConfigurationResponse } from '~/hooks/apis/offers/useGenerateBeddingConfiguration';
import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';

import { reportError } from '~/utils/reportError';

import { resolveRoomNameMatchesWithAi } from '../llmRoomNameMatcher';
import type { FlashOfferProperty } from '../types';

import { mapGeneratedBeddingConfigurationToBedGroups } from './bedding';
import { getSalesforceRoomsMissingExistingRoomTypes, mapSalesforceRoomsToRoomDrafts } from './utils';
import type { FlashOfferRoomDraft } from './utils';

export interface PrepareRoomDraftsParams {
  aiRoomNameOverrides: Record<string, string>;
  existingRoomTypes: Array<App.RoomType>;
  generateBulkBeddingConfigurations: (dealIds: Array<string>) => Promise<BulkGeneratedBeddingConfigurationResponse>;
  property: FlashOfferProperty | null;
  salesforceRooms: Array<SalesforceRoom>;
}

export interface PrepareRoomDraftsResult {
  aiRoomNameOverrides?: Record<string, string>;
  roomDrafts: Array<FlashOfferRoomDraft>;
  unmatchedSalesforceRoomNames: Array<string>;
  warningMessage?: string;
}

async function applyBeddingConfigurations(
  rooms: Array<SalesforceRoom>,
  generateBulkBeddingConfigurations: PrepareRoomDraftsParams['generateBulkBeddingConfigurations'],
) {
  const bedGroupsByDealId = new Map<string, SalesforceRoom['bed_groups']>();
  const dealIdByRoomAssetId = new Map<string, string>();
  let hasBeddingConfigurationError = false;

  rooms.forEach((room) => {
    const roomAssetId = room.room_asset?.sfid || room.room_asset?.id;
    const roomAssetKey = roomAssetId ? String(roomAssetId) : '';
    const dealId = room.id ? String(room.id) : '';

    if (roomAssetKey && dealId && !dealIdByRoomAssetId.has(roomAssetKey)) {
      dealIdByRoomAssetId.set(roomAssetKey, dealId);
    }
  });

  const dealIds = Array.from(dealIdByRoomAssetId.values());

  for (let index = 0; index < dealIds.length; index += 10) {
    let response;

    try {
      response = await generateBulkBeddingConfigurations(dealIds.slice(index, index + 10));
    } catch (error) {
      reportError(error);
      hasBeddingConfigurationError = true;
      continue;
    }

    response.result?.forEach((result) => {
      if (result.status !== 200) {
        hasBeddingConfigurationError = true;
        return;
      }

      const bedGroups = mapGeneratedBeddingConfigurationToBedGroups({ result: result.result });

      if (bedGroups.length > 0) {
        bedGroupsByDealId.set(result.deal_offer_id, bedGroups);
      }
    });
  }

  const roomsWithBedGroups = rooms.map((room) => {
    const bedGroups = bedGroupsByDealId.get(String(room.id));
    return bedGroups ? { ...room, bed_groups: bedGroups } : room;
  });

  return {
    rooms: roomsWithBedGroups,
    warningMessage: hasBeddingConfigurationError
      ? 'Some bedding configurations could not be prefilled. Review room bedding before continuing.'
      : undefined,
  };
}

/**
 * Matches the offer's Salesforce deal rooms against the property's existing room types (with an
 * AI-assisted name-matching fallback), prefills bedding configurations for whatever's still
 * missing, and turns the result into room drafts. Pure aside from the two injected service
 * calls -- the caller (useRoomsStep) owns applying the result to its own state.
 */
export async function prepareRoomDrafts({
  aiRoomNameOverrides,
  existingRoomTypes,
  generateBulkBeddingConfigurations,
  property,
  salesforceRooms,
}: PrepareRoomDraftsParams): Promise<PrepareRoomDraftsResult> {
  if (!property?.id) {
    return { roomDrafts: [], unmatchedSalesforceRoomNames: [] };
  }

  const substringMissingRooms = getSalesforceRoomsMissingExistingRoomTypes(salesforceRooms, existingRoomTypes);
  let nameOverrides = aiRoomNameOverrides;
  let resolvedOverridesToApply: Record<string, string> | undefined;

  if (substringMissingRooms.length > 0 && existingRoomTypes.length > 0) {
    const resolvedOverrides = await resolveRoomNameMatchesWithAi(
      substringMissingRooms.map((room) => room.room_asset?.name),
      existingRoomTypes.map((roomType) => roomType.name),
    );

    if (Object.keys(resolvedOverrides).length > 0) {
      nameOverrides = { ...aiRoomNameOverrides, ...resolvedOverrides };
      resolvedOverridesToApply = nameOverrides;
    }
  }

  const missingSalesforceRooms = getSalesforceRoomsMissingExistingRoomTypes(
    salesforceRooms,
    existingRoomTypes,
    nameOverrides,
  );

  if (missingSalesforceRooms.length === 0) {
    return { aiRoomNameOverrides: resolvedOverridesToApply, roomDrafts: [], unmatchedSalesforceRoomNames: [] };
  }

  const { rooms, warningMessage } = await applyBeddingConfigurations(
    missingSalesforceRooms,
    generateBulkBeddingConfigurations,
  );
  const roomDrafts = mapSalesforceRoomsToRoomDrafts({
    propertyCategory: property.category,
    propertyId: property.id,
    rooms,
  });

  return {
    aiRoomNameOverrides: resolvedOverridesToApply,
    roomDrafts,
    // Tracked separately so these names stay visible even after the CM deletes every draft.
    unmatchedSalesforceRoomNames: roomDrafts.map((draft) => draft.name).filter(Boolean),
    warningMessage,
  };
}
